import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../services/api';
import { TrashIcon, CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../components/Notification';
import { Drawer } from '../../components/ui/Drawer';

interface DeveloperActivitySummary {
  id: number;
  date: string;
  dayName: string;
  formatted: string;
  summary: string;
  commitCount: number;
  updatedAt: string | null;
}

const DrawerErrorBanner: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => (
  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
    <XCircleIcon className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-red-700 dark:text-red-300 flex-1">{message}</p>
    <button onClick={onDismiss} className="text-red-400 hover:text-red-600 dark:hover:text-red-200 flex-shrink-0">
      <span className="sr-only">Dismiss</span>
      <XCircleIcon className="w-4 h-4" />
    </button>
  </div>
);

const DeveloperActivity: React.FC = () => {
  const [data, setData] = useState<DeveloperActivitySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);

  // Edit Drawer states
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<DeveloperActivitySummary | null>(null);
  const [editSummaryText, setEditSummaryText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editDrawerError, setEditDrawerError] = useState<string | null>(null);

  const handleOpenEditDrawer = useCallback((row: DeveloperActivitySummary) => {
    setEditingRow(row);
    setEditSummaryText(row.summary);
    setEditDrawerError(null);
    setIsEditDrawerOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;

    if (!editSummaryText?.trim()) {
      setEditDrawerError('Summary text is required.');
      return;
    }

    setIsSavingEdit(true);
    setEditDrawerError(null);

    try {
      const response = await api.put(`/git-activity/summaries/${editingRow.id}`, {
        summary: editSummaryText,
      });

      const updated = response.data.data;
      setData(prev =>
        prev.map(item =>
          item.id === editingRow.id
            ? { ...item, summary: updated.summary, updatedAt: updated.updatedAt }
            : item
        )
      );

      setSuccessNotification({
        title: 'Updated Successfully',
        message: `Summary for ${editingRow.date} has been updated.`,
      });
      closeEditDrawer();
    } catch (error: any) {
      console.error('Error saving summary:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setEditDrawerError(errorMessage);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingRow, editSummaryText]);

  const handleDelete = useCallback(async (row: DeveloperActivitySummary) => {
    if (!window.confirm(`Delete summary for ${row.date} (${row.dayName})? It will be regenerated on next sync.`)) {
      return;
    }

    try {
      await api.delete(`/git-activity/summaries/${row.id}`);
      setData(prev => prev.filter(item => item.id !== row.id));
      setSuccessNotification({
        title: 'Deleted Successfully',
        message: `Summary for ${row.date} has been deleted. It will regenerate on the next sync.`,
      });
    } catch (error: any) {
      console.error('Error deleting summary:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({
        title: 'Failed to Delete',
        message: `Could not delete summary: ${errorMessage}`,
      });
    }
  }, []);

  const columns = useMemo<ColumnDef<DeveloperActivitySummary>[]>(
    () => [
      {
        accessorKey: 'date',
        header: 'Date',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
        cell: ({ row }) => (
          <div>
            <span className="font-semibold text-blue-600 dark:text-blue-400">{row.original.date}</span>
            <span className="ml-2 text-xs text-muted">{row.original.dayName}</span>
          </div>
        ),
      },
      {
        accessorKey: 'summary',
        header: 'Summary',
        enableSorting: false,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 600,
        cell: ({ row }) => (
          <div className="whitespace-pre-wrap text-sm text-label max-h-32 overflow-y-auto">
            {row.original.summary}
          </div>
        ),
      },
      {
        accessorKey: 'commitCount',
        header: 'Commits',
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
        cell: ({ row }) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
            {row.original.commitCount}
          </span>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        enableSorting: true,
        enableColumnFilter: false,
        size: 160,
        cell: ({ row }) => (
          <span className="text-xs text-muted">
            {row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString() : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        // Row click opens the editor; delete stays here (stopPropagation so it
        // doesn't also open the drawer).
        cell: ({ row }) => (
          <div className="flex gap-2 justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(row.original); }}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              title="Delete (will regenerate on next sync)"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [handleDelete]
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/git-activity/summaries?days=365');
      setData(response.data.summaries || []);
    } catch (error) {
      console.error('Error loading developer activity summaries:', error);
      setErrorNotification({
        title: 'Failed to Load Data',
        message: 'Could not load developer activity summaries from the server.',
      });
    } finally {
      setLoading(false);
    }
  };

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setEditingRow(null);
    setEditSummaryText('');
    setEditDrawerError(null);
  };

  return (
    <div className="h-full w-full p-10 pb-24">
      <TanTable
        data={data}
        columns={columns}
        loading={loading}
        settingsKey="developer-activity-summary-table"
        fieldName="developer-activity-summary-table"
        searchPlaceholder="Search summaries..."
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Developer Activity Summaries"
        onRowClick={handleOpenEditDrawer}
        lockedColumns={[{ id: 'actions', side: 'right' }]}
      />

      {/* Edit Drawer */}
      <Drawer
        isOpen={isEditDrawerOpen}
        onClose={closeEditDrawer}
        title={editingRow ? `Edit Summary — ${editingRow.date} (${editingRow.dayName})` : 'Edit Summary'}
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={closeEditDrawer}
              disabled={isSavingEdit}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSavingEdit ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          {editDrawerError && (
            <DrawerErrorBanner message={editDrawerError} onDismiss={() => setEditDrawerError(null)} />
          )}

          {editingRow && (
            <div className="text-sm text-muted">
              {editingRow.commitCount} commit{editingRow.commitCount !== 1 ? 's' : ''} on this date
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Summary <span className="text-red-500">*</span>
            </label>
            <textarea
              value={editSummaryText}
              onChange={(e) => setEditSummaryText(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
              disabled={isSavingEdit}
            />
          </div>
        </div>
      </Drawer>

      {/* Page-level notifications */}
      {errorNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>,
        document.body
      )}

      {successNotification && createPortal(
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>,
        document.body
      )}
    </div>
  );
};

export default DeveloperActivity;
